import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store/configureStore';
import {
  getCreatePlaylistAudioTemplatedLink,
  getPlaylistsList,
} from 'app/store/Playlists/selectors';
import { PlaylistAudio, PlaylistsListItem } from 'types/playlists.types';
import { Link } from 'types/api.types';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  getFailureMessage,
  getSuccessMessage,
  audioToSelectedAudioObject,
} from 'app/components/Song/Audio/CreatePlaylistDialog/utils';
import { CreatePlaylistDialogComponent } from 'app/components/Song/Audio/CreatePlaylistDialog/CreatePlaylistDialogComponent';
import {
  useCreatePlaylist,
  useCreatePlaylistLimit,
} from 'app/components/Playlist/hooks';
import { setShowUpgradePopup } from 'app/store/Storage/slice';
import { AudioSelectionItem } from 'app/components/Audio';
import {
  addPlaylistAudiosEffect,
  fetchPlaylistsEffect,
} from 'app/store/Playlists/effects';

interface CreatePlaylistDialogProps {
  open: boolean;
  onClose: () => void;
  audio: AudioSelectionItem;
}

export const CreatePlaylistDialog = ({
  audio,
  open,
  onClose,
}: CreatePlaylistDialogProps) => {
  const dispatch = useAppDispatch();
  const playlists = useAppSelector(getPlaylistsList);
  const addAudioLink = useAppSelector(getCreatePlaylistAudioTemplatedLink);
  const [isListLoading, setIsListLoading] = useState(true);
  const { isLimitReached } = useCreatePlaylistLimit();
  const { maxPlaylistAudios, handleOnAdd } = useCreatePlaylist(audio.parent);

  const fetchPlaylists = useCallback(async () => {
    try {
      await dispatch(fetchPlaylistsEffect());
    } finally {
      setIsListLoading(false);
    }
  }, [dispatch]);

  const addAudioToPlaylists = useCallback(
    async (playlists: PlaylistsListItem[]) => {
      const requestDetails = await Promise.allSettled(
        playlists.map((playlist) => {
          const link: Link = {
            ...addAudioLink,
            href:
              addAudioLink?.href.replace('{playlistGuid}', playlist.guid) || '',
          };
          return dispatch(
            addPlaylistAudiosEffect({
              audios: audioToSelectedAudioObject(audio),
              playlistGuid: playlist.guid,
              link,
              context: 'SONG_ROOM',
            })
          ).unwrap();
        })
      );

      const succeeded = requestDetails.filter(
        (el) => el.status === 'fulfilled'
      );
      const failed = requestDetails.filter((el) => el.status === 'rejected');

      if (!failed.length) {
        dispatch(addAlertAction({ message: getSuccessMessage(playlists) }));
      } else {
        dispatch(
          addAlertAction({
            message: getFailureMessage(failed.length, playlists.length),
          })
        );
      }

      return {
        failedCount: failed.length,
        addedPlaylists: succeeded
          .map((el) => {
            if (el.status !== 'rejected') {
              const audios = (el.value as unknown) as PlaylistAudio[];
              return audios.map((audio) => audio.playlistGuid);
            }
            return [];
          })
          .flat(),
      };
    },
    [dispatch, audio, addAudioLink]
  );

  useEffect(() => {
    fetchPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CreatePlaylistDialogComponent
      open={open}
      playlists={playlists}
      playlistsLoading={isListLoading}
      limitReached={isLimitReached}
      maxPlaylistAudiosCount={maxPlaylistAudios}
      addAudioToPlaylists={addAudioToPlaylists}
      createPlaylist={() => handleOnAdd(audioToSelectedAudioObject(audio))}
      onClose={onClose}
      showLimitReachedPopup={() =>
        dispatch(
          setShowUpgradePopup({
            messageType: 'playlist',
            disableOverlay: true,
            context: {
              action: 'createPlaylist',
            },
          })
        )
      }
    />
  );
};
